import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { Users, Search, ArrowUpDown, ArrowUp, ArrowDown, Download, LayoutGrid, Table as TableIcon, Loader2, AlertCircle, UserCheck } from 'lucide-react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { getStudentPhotoUrl } from '../../utils/mediaUrls';

const MenteeYaadiWidget = ({ userEmail, userName }) => {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('rollNumber');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filterDivision, setFilterDivision] = useState('All');
  const [divisions, setDivisions] = useState([]);
  const [viewMode, setViewMode] = useState('table');
  const [assignedBatches, setAssignedBatches] = useState([]);

  useEffect(() => {
    if (userEmail) {
      fetchMentees();
    } else {
      setError('User email not provided. Please log in again.');
      setLoading(false);
    }
  }, [userEmail]);

  useEffect(() => {
    applyFiltersAndSort();
  }, [students, searchTerm, sortField, sortDirection, filterDivision]);

  const fetchMentees = async () => {
    try {
      setLoading(true);
      setError(null);

      const batchAssignmentsRef = collection(db, 'batchAssignments');
      const mentorQuery = query(batchAssignmentsRef, where('mentorEmail', '==', userEmail));
      const assignmentsSnapshot = await getDocs(mentorQuery);

      if (assignmentsSnapshot.empty) {
        setError('No batch assignments found. Please contact the Mentoring Coordinator.');
        setLoading(false);
        return;
      }

      const batchDivisionPairs = [];
      assignmentsSnapshot.forEach((doc) => {
        const data = doc.data();
        batchDivisionPairs.push({
          batch: data.batch,
          division: data.division
        });
      });
      setAssignedBatches(batchDivisionPairs);

      const studentsRef = collection(db, 'students');
      const studentsQuery = query(studentsRef, orderBy('rollNumber', 'asc'));
      const studentsSnapshot = await getDocs(studentsQuery);

      const menteesList = [];
      const divisionSet = new Set();

      studentsSnapshot.forEach((doc) => {
        const studentData = { id: doc.id, ...doc.data() };
        
        const isMyMentee = batchDivisionPairs.some(
          pair => studentData.batch === pair.batch && studentData.division === pair.division
        );

        if (isMyMentee) {
          menteesList.push(studentData);
          if (studentData.division) {
            divisionSet.add(studentData.division);
          }
        }
      });

      setStudents(menteesList);
      setDivisions(['All', ...Array.from(divisionSet).sort()]);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching mentees:', err);
      setError('Failed to load mentees. Please try again.');
      setLoading(false);
    }
  };

  const applyFiltersAndSort = () => {
    let filtered = [...students];

    if (searchTerm) {
      filtered = filtered.filter(student => {
        const searchLower = searchTerm.toLowerCase();
        return (
          student.rollNumber?.toLowerCase().includes(searchLower) ||
          student.firstName?.toLowerCase().includes(searchLower) ||
          student.surname?.toLowerCase().includes(searchLower) ||
          student.email?.toLowerCase().includes(searchLower) ||
          `${student.firstName} ${student.surname}`.toLowerCase().includes(searchLower)
        );
      });
    }

    if (filterDivision !== 'All') {
      filtered = filtered.filter(s => s.division === filterDivision);
    }

    filtered.sort((a, b) => {
      let aValue = a[sortField] || '';
      let bValue = b[sortField] || '';

      if (sortField === 'name') {
        aValue = `${a.firstName || ''} ${a.surname || ''}`.trim();
        bValue = `${b.firstName || ''} ${b.surname || ''}`.trim();
      }

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredStudents(filtered);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-4 h-4 text-gray-400" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-4 h-4 text-blue-600" />
      : <ArrowDown className="w-4 h-4 text-blue-600" />;
  };

  const downloadPDF = () => {
    const doc = new jsPDF('landscape', 'mm', 'a4');
    
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(`My Mentees - ${userName || 'Mentor'}`, 14, 15);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const today = new Date().toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
    doc.text(`Generated on: ${today}`, 14, 22);
    
    if (filterDivision !== 'All') {
      doc.text(`Division: ${filterDivision}`, 14, 27);
    }
    
    const batchInfo = assignedBatches.map(b => `${b.batch}-${b.division}`).join(', ');
    doc.text(`Assigned Batches: ${batchInfo}`, 14, filterDivision !== 'All' ? 32 : 27);
    
    const tableData = filteredStudents.map(student => [
      student.rollNumber || '-',
      `${student.firstName || ''} ${student.surname || ''}`.trim() || '-',
      student.division || '-',
      student.batch || '-',
      student.email || '-',
      student.mobile || '-',
      student.bloodGroup || '-',
      student.gender || '-'
    ]);

    doc.autoTable({
      head: [['Roll Number', 'Name', 'Division', 'Batch', 'Email', 'Mobile', 'Blood Group', 'Gender']],
      body: tableData,
      startY: filterDivision !== 'All' ? 37 : 32,
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: {
        fillColor: [37, 99, 235],
        fontStyle: 'bold',
        halign: 'center'
      },
      alternateRowStyles: { fillColor: [245, 247, 250] }
    });

    const filename = `My_Mentees_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(filename);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Loading your mentees...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <div className="flex items-center gap-3 mb-3">
            <AlertCircle className="w-6 h-6 text-red-600" />
            <h3 className="text-red-800 font-semibold text-lg">Error Loading Mentees</h3>
          </div>
          <p className="text-red-700 text-sm mb-4">{error}</p>
          <button 
            onClick={fetchMentees}
            className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (students.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center max-w-md">
          <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-800 mb-2">No Mentees Assigned</h3>
          <p className="text-gray-600 text-sm leading-relaxed">
            You don't have any students assigned to you yet. Please contact the Mentoring Coordinator for batch assignments.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .thick-scrollbar::-webkit-scrollbar {
          width: 16px;
          height: 16px;
        }
        .thick-scrollbar::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 10px;
          border: 2px solid #e2e8f0;
        }
        .thick-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(180deg, #3b82f6 0%, #2563eb 100%);
          border-radius: 10px;
          border: 2px solid #f1f5f9;
        }
        .thick-scrollbar::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(180deg, #2563eb 0%, #1d4ed8 100%);
        }
        .thick-scrollbar::-webkit-scrollbar-corner {
          background: #f1f5f9;
        }
        .thick-scrollbar {
          scrollbar-width: thick;
          scrollbar-color: #3b82f6 #f1f5f9;
        }
      `}</style>

      <div className="space-y-4">
        <div className="bg-gradient-to-r from-blue-600 to-blue-500 rounded-lg shadow-md p-6 text-white">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3">
                <UserCheck className="w-7 h-7" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">My Mentees</h2>
                <p className="text-blue-100 text-sm mt-1">
                  {filteredStudents.length} student{filteredStudents.length !== 1 ? 's' : ''} • 
                  {assignedBatches.map(b => ` ${b.batch}-${b.division}`).join(', ')}
                </p>
              </div>
            </div>
            
            <button
              onClick={downloadPDF}
              className="flex items-center gap-2 px-5 py-2.5 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-all font-medium shadow-lg hover:shadow-xl"
            >
              <Download className="w-4 h-4" />
              Export PDF
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[250px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, roll number, or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Division:</label>
              <select
                value={filterDivision}
                onChange={(e) => setFilterDivision(e.target.value)}
                className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm font-medium bg-white"
              >
                {divisions.map(div => (
                  <option key={div} value={div}>{div}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md transition-all ${
                  viewMode === 'grid'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title="Grid View"
              >
                <LayoutGrid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`p-2 rounded-md transition-all ${
                  viewMode === 'table'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title="Table View"
              >
                <TableIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {searchTerm && (
          <div className="text-sm text-gray-600 px-1">
            Showing {filteredStudents.length} of {students.length} students
          </div>
        )}

        {viewMode === 'table' ? (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto overflow-y-auto max-h-[70vh] thick-scrollbar">
              <table className="min-w-full divide-y-4 divide-gray-400">
                <thead className="sticky top-0 z-10">
                  <tr>
                    <th 
                      onClick={() => handleSort('rollNumber')}
                      className="px-5 py-3.5 text-left text-xs font-bold text-white uppercase tracking-wide cursor-pointer hover:opacity-90 transition-all border-r-4 border-white bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg"
                    >
                      <div className="flex items-center gap-2">
                        Roll Number
                        {getSortIcon('rollNumber')}
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('surname')}
                      className="px-5 py-3.5 text-left text-xs font-bold text-white uppercase tracking-wide cursor-pointer hover:opacity-90 transition-all border-r-4 border-white bg-gradient-to-br from-purple-500 to-purple-600 shadow-lg"
                    >
                      <div className="flex items-center gap-2">
                        Surname
                        {getSortIcon('surname')}
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('firstName')}
                      className="px-5 py-3.5 text-left text-xs font-bold text-white uppercase tracking-wide cursor-pointer hover:opacity-90 transition-all border-r-4 border-white whitespace-nowrap bg-gradient-to-br from-pink-500 to-pink-600 shadow-lg"
                    >
                      <div className="flex items-center gap-2">
                        First Name
                        {getSortIcon('firstName')}
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('fatherName')}
                      className="px-5 py-3.5 text-left text-xs font-bold text-white uppercase tracking-wide cursor-pointer hover:opacity-90 transition-all border-r-4 border-white whitespace-nowrap bg-gradient-to-br from-indigo-500 to-indigo-600 shadow-lg"
                    >
                      <div className="flex items-center gap-2">
                        Father Name
                        {getSortIcon('fatherName')}
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('dob')}
                      className="px-5 py-3.5 text-left text-xs font-bold text-white uppercase tracking-wide cursor-pointer hover:opacity-90 transition-all border-r-4 border-white bg-gradient-to-br from-green-500 to-green-600 shadow-lg"
                    >
                      <div className="flex items-center gap-2">
                        DOB
                        {getSortIcon('dob')}
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('gender')}
                      className="px-5 py-3.5 text-left text-xs font-bold text-white uppercase tracking-wide cursor-pointer hover:opacity-90 transition-all border-r-4 border-white bg-gradient-to-br from-teal-500 to-teal-600 shadow-lg"
                    >
                      <div className="flex items-center gap-2">
                        Gender
                        {getSortIcon('gender')}
                      </div>
                    </th>
                    <th className="px-5 py-3.5 text-left text-xs font-bold text-white uppercase tracking-wide border-r-4 border-white whitespace-nowrap bg-gradient-to-br from-cyan-500 to-cyan-600 shadow-lg">
                      Current Address
                    </th>
                    <th className="px-5 py-3.5 text-left text-xs font-bold text-white uppercase tracking-wide border-r-4 border-white whitespace-nowrap bg-gradient-to-br from-sky-500 to-sky-600 shadow-lg">
                      Permanent Address
                    </th>
                    <th className="px-5 py-3.5 text-left text-xs font-bold text-white uppercase tracking-wide border-r-4 border-white whitespace-nowrap bg-gradient-to-br from-amber-500 to-amber-600 shadow-lg">
                      Aadhar Card
                    </th>
                    <th 
                      onClick={() => handleSort('bloodGroup')}
                      className="px-5 py-3.5 text-left text-xs font-bold text-white uppercase tracking-wide cursor-pointer hover:opacity-90 transition-all border-r-4 border-white whitespace-nowrap bg-gradient-to-br from-red-500 to-red-600 shadow-lg"
                    >
                      <div className="flex items-center gap-2">
                        Blood Group
                        {getSortIcon('bloodGroup')}
                      </div>
                    </th>
                    <th className="px-5 py-3.5 text-left text-xs font-bold text-white uppercase tracking-wide border-r-4 border-white whitespace-nowrap bg-gradient-to-br from-orange-500 to-orange-600 shadow-lg">
                      ABC ID
                    </th>
                    <th className="px-5 py-3.5 text-left text-xs font-bold text-gray-800 uppercase tracking-wide border-r-4 border-white bg-gradient-to-br from-lime-400 to-lime-500 shadow-lg">
                      Hobbies
                    </th>
                    <th className="px-5 py-3.5 text-left text-xs font-bold text-white uppercase tracking-wide border-r-4 border-white bg-gradient-to-br from-emerald-500 to-emerald-600 shadow-lg">
                      Mobile
                    </th>
                    <th className="px-5 py-3.5 text-left text-xs font-bold text-white uppercase tracking-wide border-r-4 border-white bg-gradient-to-br from-green-600 to-green-700 shadow-lg">
                      WhatsApp
                    </th>
                    <th className="px-5 py-3.5 text-left text-xs font-bold text-white uppercase tracking-wide border-r-4 border-white bg-gradient-to-br from-blue-600 to-blue-700 shadow-lg">
                      Email
                    </th>
                    <th className="px-5 py-3.5 text-left text-xs font-bold text-white uppercase tracking-wide border-r-4 border-white whitespace-nowrap bg-gradient-to-br from-violet-500 to-violet-600 shadow-lg">
                      Father Mobile
                    </th>
                    <th className="px-5 py-3.5 text-left text-xs font-bold text-white uppercase tracking-wide border-r-4 border-white whitespace-nowrap bg-gradient-to-br from-fuchsia-500 to-fuchsia-600 shadow-lg">
                      Father Occupation
                    </th>
                    <th className="px-5 py-3.5 text-left text-xs font-bold text-white uppercase tracking-wide border-r-4 border-white whitespace-nowrap bg-gradient-to-br from-rose-500 to-rose-600 shadow-lg">
                      Mother Name
                    </th>
                    <th className="px-5 py-3.5 text-left text-xs font-bold text-white uppercase tracking-wide border-r-4 border-white whitespace-nowrap bg-gradient-to-br from-pink-600 to-pink-700 shadow-lg">
                      Mother Mobile
                    </th>
                    <th className="px-5 py-3.5 text-left text-xs font-bold text-white uppercase tracking-wide whitespace-nowrap bg-gradient-to-br from-purple-600 to-purple-700 shadow-lg">
                      Mother Occupation
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y-2 divide-gray-300">
                  {filteredStudents.map((student) => (
                    <tr 
                      key={student.id}
                      className="hover:bg-gray-50 transition-all duration-150"
                    >
                      <td className="px-5 py-3.5 text-sm font-bold text-gray-900 whitespace-nowrap border-r-4 border-gray-300 bg-blue-50">
                        {student.rollNumber || '-'}
                      </td>
                      <td className="px-5 py-3.5 text-sm text-gray-900 whitespace-nowrap border-r-4 border-gray-300 bg-purple-50">
                        {student.surname || '-'}
                      </td>
                      <td className="px-5 py-3.5 text-sm text-gray-900 whitespace-nowrap border-r-4 border-gray-300 bg-pink-50">
                        {student.firstName || '-'}
                      </td>
                      <td className="px-5 py-3.5 text-sm text-gray-900 whitespace-nowrap border-r-4 border-gray-300 bg-indigo-50">
                        {student.fatherName || '-'}
                      </td>
                      <td className="px-5 py-3.5 text-sm text-gray-900 whitespace-nowrap border-r-4 border-gray-300 bg-green-50">
                        {student.dob || '-'}
                      </td>
                      <td className="px-5 py-3.5 text-sm text-gray-900 border-r-4 border-gray-300 bg-teal-50">
                        {student.gender || '-'}
                      </td>
                      <td className="px-5 py-3.5 text-sm text-gray-800 border-r-4 border-gray-300 max-w-xs bg-cyan-50">
                        <div className="truncate" title={student.currentAddress}>
                          {student.currentAddress || '-'}
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-sm text-gray-800 border-r-4 border-gray-300 max-w-xs bg-sky-50">
                        <div className="truncate" title={student.permanentAddress}>
                          {student.permanentAddress || '-'}
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-sm text-gray-900 whitespace-nowrap border-r-4 border-gray-300 bg-amber-50">
                        {student.aadharCard || '-'}
                      </td>
                      <td className="px-5 py-3.5 text-sm font-semibold text-red-800 whitespace-nowrap border-r-4 border-gray-300 bg-red-50">
                        {student.bloodGroup || '-'}
                      </td>
                      <td className="px-5 py-3.5 text-sm text-gray-900 whitespace-nowrap border-r-4 border-gray-300 bg-orange-50">
                        {student.abcId || '-'}
                      </td>
                      <td className="px-5 py-3.5 text-sm text-gray-800 border-r-4 border-gray-300 max-w-xs bg-lime-50">
                        <div className="truncate" title={student.hobbies}>
                          {student.hobbies || '-'}
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-sm text-gray-900 whitespace-nowrap border-r-4 border-gray-300 bg-emerald-50">
                        {student.mobile || '-'}
                      </td>
                      <td className="px-5 py-3.5 text-sm text-gray-900 whitespace-nowrap border-r-4 border-gray-300 bg-green-50">
                        {student.whatsapp || student.mobile || '-'}
                      </td>
                      <td className="px-5 py-3.5 text-sm text-gray-800 border-r-4 border-gray-300 max-w-xs bg-blue-50">
                        <div className="truncate" title={student.email}>
                          {student.email || '-'}
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-sm text-gray-900 whitespace-nowrap border-r-4 border-gray-300 bg-violet-50">
                        {student.fatherMobile || '-'}
                      </td>
                      <td className="px-5 py-3.5 text-sm text-gray-800 border-r-4 border-gray-300 max-w-xs bg-fuchsia-50">
                        <div className="truncate" title={student.fatherOccupation}>
                          {student.fatherOccupation || '-'}
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-sm text-gray-900 whitespace-nowrap border-r-4 border-gray-300 bg-rose-50">
                        {student.motherName || '-'}
                      </td>
                      <td className="px-5 py-3.5 text-sm text-gray-900 whitespace-nowrap border-r-4 border-gray-300 bg-pink-50">
                        {student.motherMobile || '-'}
                      </td>
                      <td className="px-5 py-3.5 text-sm text-gray-800 max-w-xs bg-purple-50">
                        <div className="truncate" title={student.motherOccupation}>
                          {student.motherOccupation || '-'}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredStudents.map((student) => (
              <div 
                key={student.id}
                className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all overflow-hidden border border-gray-200"
              >
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-4 text-white">
                  <div className="flex items-center gap-3">
                    {getStudentPhotoUrl(student) ? (
                      <img 
                        src={getStudentPhotoUrl(student)} 
                        alt={`${student.firstName} ${student.surname}`}
                        className="w-12 h-12 rounded-full object-cover border-2 border-white/30 shadow-lg"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className="bg-white/20 backdrop-blur-sm rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl shadow-lg"
                      style={{ display: getStudentPhotoUrl(student) ? 'none' : 'flex' }}
                    >
                      {student.firstName?.[0]}{student.surname?.[0]}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-lg truncate">
                        {student.firstName} {student.surname}
                      </h3>
                      <p className="text-blue-100 text-sm font-medium">
                        {student.rollNumber}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="p-4 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 font-medium">Division:</span>
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded font-semibold">
                      {student.division || '-'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 font-medium">Batch:</span>
                    <span className="text-gray-900 font-semibold">{student.batch || '-'}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 font-medium">Blood Group:</span>
                    <span className="px-2 py-1 bg-red-100 text-red-800 rounded font-semibold">
                      {student.bloodGroup || '-'}
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200 space-y-2">
                    <div className="space-y-1">
                      <p className="text-xs font-semibold text-blue-600 uppercase mb-1">Student Contact</p>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-500 font-medium">📱 Mobile:</span>
                        <span className="text-gray-900 font-semibold">{student.mobile || '-'}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-500 font-medium">💬 WhatsApp:</span>
                        <span className="text-gray-900 font-semibold">{student.whatsapp || student.mobile || '-'}</span>
                      </div>
                      <p className="text-xs text-gray-600 truncate pt-1" title={student.email}>
                        📧 {student.email || '-'}
                      </p>
                    </div>
                    
                    <div className="space-y-1 pt-2 border-t border-gray-100">
                      <p className="text-xs font-semibold text-purple-600 uppercase mb-1">Parent Contact</p>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-500 font-medium">👨 Father Mobile:</span>
                        <span className="text-gray-900 font-semibold">{student.fatherMobile || '-'}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-500 font-medium">💬 Father WhatsApp:</span>
                        <span className="text-gray-900 font-semibold">{student.fatherWhatsapp || student.fatherMobile || '-'}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-500 font-medium">👩 Mother Mobile:</span>
                        <span className="text-gray-900 font-semibold">{student.motherMobile || '-'}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-500 font-medium">💬 Mother WhatsApp:</span>
                        <span className="text-gray-900 font-semibold">{student.motherWhatsapp || student.motherMobile || '-'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {filteredStudents.length === 0 && students.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-800 mb-2">No students found</h3>
            <p className="text-gray-600 text-sm">
              Try adjusting your search or filter criteria
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default MenteeYaadiWidget;
